import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Link } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import { API_BASE_URL } from '../utils';

const STYLES = {
  info: {
    label: 'Info', Icon: InfoOutlinedIcon,
    bg: '#EFF6FF', border: '#DBEAFE', fg: '#1E3A8A', strong: '#2563EB', iconBg: 'rgba(37,99,235,0.12)',
  },
  warning: {
    label: 'Warning', Icon: WarningAmberOutlinedIcon,
    bg: '#FFFBEB', border: '#FEF3C7', fg: '#92400E', strong: '#D97706', iconBg: 'rgba(217,119,6,0.14)',
  },
  success: {
    label: 'Success', Icon: CheckCircleOutlineIcon,
    bg: '#ECFDF5', border: '#D1FAE5', fg: '#065F46', strong: '#059669', iconBg: 'rgba(5,150,105,0.14)',
  },
};

/** App-wide announcement banner. Fetches live announcements and lets the user
 *  dismiss each for the session. Renders nothing when there's nothing to show. */
export default function AnnouncementBanner() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissedAnnouncements') || '[]'); } catch { return []; }
  });

  useEffect(() => {
    let active = true;
    axios.get(`${API_BASE_URL}/api/announcements`)
      .then((res) => { if (active && Array.isArray(res.data?.announcements)) setItems(res.data.announcements); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try { sessionStorage.setItem('dismissedAnnouncements', JSON.stringify(next)); } catch (e) { /* ignore */ }
  };

  const visible = items.filter((a) => !dismissed.includes(a.id));
  if (!visible.length) return null;

  return (
    <Box>
      {visible.map((a) => {
        const s = STYLES[a.severity] || STYLES.info;
        const SevIcon = s.Icon;
        return (
          <Box key={a.id} sx={{ position: 'relative', bgcolor: s.bg, borderBottom: `1px solid ${s.border}` }}>
            {/* severity accent rail */}
            <Box aria-hidden sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: s.strong }} />
            <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1.75, md: 3 }, py: 1.05, display: 'flex', alignItems: 'center', gap: 1.25 }}>
              {/* icon badge */}
              <Box sx={{ flexShrink: 0, width: 28, height: 28, borderRadius: '9px', display: 'grid', placeItems: 'center', bgcolor: s.iconBg, color: s.strong }}>
                <SevIcon sx={{ fontSize: 18 }} aria-label={a.severity} titleAccess={a.severity} />
              </Box>
              {/* severity pill */}
              <Box component="span" sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-block' }, px: 1, py: '2px', borderRadius: '999px', fontSize: '0.64rem', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: s.strong, bgcolor: s.iconBg, fontFamily: '"Inter","Roboto",sans-serif' }}>
                {s.label}
              </Box>
              {/* message */}
              <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.86rem', fontWeight: 600, color: s.fg, fontFamily: '"Inter","Roboto",sans-serif' }}>
                {a.message}
              </Typography>
              {/* CTA */}
              {a.link_url && (
                <Link href={a.link_url} target="_blank" rel="noopener"
                  sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', gap: 0.4, px: 1.4, py: 0.5, borderRadius: '8px', fontSize: '0.76rem', fontWeight: 800, color: '#fff', bgcolor: s.strong, textDecoration: 'none', whiteSpace: 'nowrap', fontFamily: '"Inter","Roboto",sans-serif', '&:hover': { filter: 'brightness(0.94)' } }}>
                  {a.link_label || 'Learn more'} →
                </Link>
              )}
              {/* dismiss */}
              <IconButton size="small" aria-label="Dismiss" onClick={() => dismiss(a.id)} sx={{ flexShrink: 0, color: s.fg, opacity: 0.7, '&:hover': { opacity: 1, bgcolor: s.iconBg } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
